import { LOG_GROUPS, LOG_LEVEL, getConnectionDispatchers, isSeamlessConnectionAvailable, storage } from '@b2x/shared';
import { initializeStore } from '@seamless/store';

import type { AuthenticationState } from '@b2x/authentication-library';
import type { Nullable, ProfileDispatchers, ProfileState } from '../types/profile-type';

import { resolveBusinessContext } from '../lib/condition';
import { ProfileConnection } from '../lib/connections/profile-connection';
import { defineActiveProfile } from '../lib/profile';
import {
  ONE_CONTEXT_PROFILE_CONNECTION,
  ONE_CONTEXT_TENANT,
  PROFILE_CONNECTION,
  PROFILE_PERSISTED_CONNECTION,
} from '../utilities/constants';
import { logger } from '../utilities/logger';
import { resolveAvailableProfiles } from './resolve-available-profiles';

import { getDispatchers as getOneContextDispatchers } from '@seamless/one-context';

// called on auth connection change
export async function initializeProfileConnection(authState: AuthenticationState) {
  const store = initializeStore();

  const defaultProfileState: Nullable<ProfileState> = {
    activeProfile: null,
    availableProfiles: [],
    source: null,
    meta: null,
  };

  const profileState: ProfileState = storage.session.get<ProfileState>(
    PROFILE_PERSISTED_CONNECTION,
    defaultProfileState as ProfileState,
  );

  logger('Initialize Profile Connection', {
    args: [Boolean(authState.jwe), authState.identityProvider],
    group: LOG_GROUPS.BOOTSTRAP,
    level: LOG_LEVEL.LOG,
  });

  // resolves business type, profile id and source
  const businessContext = await resolveBusinessContext();

  logger('Resolved business context', {
    args: [businessContext],
    group: LOG_GROUPS.BOOTSTRAP,
    level: LOG_LEVEL.INFO,
  });

  const resolvedAvailableProfiles = await resolveAvailableProfiles(authState, profileState, businessContext);
  const resolvedActiveProfile = defineActiveProfile(businessContext, resolvedAvailableProfiles.profiles);

  logger('Resolved Active and Available Profiles', {
    args: [resolvedActiveProfile, resolvedAvailableProfiles],
    group: LOG_GROUPS.BOOTSTRAP,
    level: LOG_LEVEL.INFO,
  });

  const definedProfileState: ProfileState = {
    availableProfiles: resolvedAvailableProfiles.profiles,
    activeProfile: resolvedActiveProfile,
    source: businessContext.source,
    meta: resolvedAvailableProfiles.meta,
  };

  // emit an event with defined profile state
  if (await isSeamlessConnectionAvailable(PROFILE_CONNECTION, 1)) {
    const profileDispatchers = await getConnectionDispatchers<ProfileDispatchers>(PROFILE_CONNECTION);
    profileDispatchers.setStateProfile(definedProfileState);
  } else {
    await store.addConnection(new ProfileConnection(definedProfileState));
  }

  // populate One Context
  const { setProfileState } = await getOneContextDispatchers(ONE_CONTEXT_TENANT, ONE_CONTEXT_PROFILE_CONNECTION);

  setProfileState({
    activeProfile: resolvedActiveProfile,
    availableProfiles: resolvedAvailableProfiles.profiles,
    meta: resolvedAvailableProfiles.meta,
  });
}
